import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bot } from 'lucide-react';
import { arenaAudio } from '../../services/ArenaAudioService';

export interface BostonTrainPassenger {
  name: string;
  avatarUrl?: string;
  isBot?: boolean;
}

interface BostonTrainAnimationProps {
  isOpen: boolean;
  isUserTeam?: boolean;
  winningTeam?: number;
  losers?: BostonTrainPassenger[];
}

const STEAM_PUFFS = [
  { delay: 0.0, size: 14 },
  { delay: 0.35, size: 18 },
  { delay: 0.7, size: 22 },
  { delay: 1.05, size: 26 },
];

const BostonTrainAnimationComponent: React.FC<BostonTrainAnimationProps> = ({
  isOpen,
  isUserTeam = true,
  winningTeam = 1,
  losers,
}) => {
  const whistlePlayedRef = useRef(false);

  useEffect(() => {
    if (isOpen && !whistlePlayedRef.current) {
      whistlePlayedRef.current = true;
      // Slight delay so it blends harmoniously with prize tone / celebration
      const timer = window.setTimeout(() => {
        try {
          arenaAudio.playTrainWhistle();
        } catch {
          // ignore audio failure
        }
      }, 400);
      return () => window.clearTimeout(timer);
    }
    if (!isOpen) {
      whistlePlayedRef.current = false;
    }
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="boston-train-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          data-testid="boston-train-animation"
          className="absolute top-[96px] sm:top-[112px] md:top-[122px] left-0 w-full overflow-hidden pointer-events-none z-[85] h-36 sm:h-40 md:h-44 select-none flex items-end pb-2.5"
          aria-label="Boston Train"
          style={{ contain: 'paint' }}
        >
          {/* Subtle glowing railway track line */}
          <div className="absolute bottom-2.5 left-0 w-full h-[3px] bg-gradient-to-r from-transparent via-amber-400/40 to-transparent shadow-[0_0_8px_rgba(245,158,11,0.5)] pointer-events-none" />

          {/* Full train assembly traversing across screen from left to right (Compositor-driven) */}
          <div className="boston-train-traverse relative flex items-end pl-4 will-change-transform">
            {/* Chugging mechanical bounce container */}
            <div className="boston-train-chug relative flex items-end will-change-transform">
              {/* Ground contact shadow under carriages (High performance replacement for filter:drop-shadow) */}
              <div className="absolute -bottom-1 left-2 right-4 h-2 bg-black/45 rounded-full blur-[2px] pointer-events-none" />

              {/* --- PASSENGER COACH WITH 'BOSTON' BANNER & ENLARGED LOSER PASSENGERS --- */}
              <div className="relative flex flex-col items-center mr-1">
                {/* Vintage Marquee Roof Banner */}
                <div
                  className="boston-train-marquee mb-1 px-4 py-1 rounded-full bg-gradient-to-r from-amber-600 via-amber-300 to-amber-600 border-2 border-amber-200 shadow-[0_0_15px_rgba(245,158,11,0.7),0_2px_6px_rgba(0,0,0,0.9)] flex items-center gap-2 whitespace-nowrap z-20"
                >
                  <span className="text-amber-950 text-xs sm:text-sm font-black animate-pulse">★</span>
                  <span
                    data-testid="all-aboard-banner"
                    className="text-xs sm:text-sm font-black uppercase tracking-widest text-slate-950 font-serif"
                  >
                    BOSTON
                  </span>
                  <span className="text-amber-950 text-xs sm:text-sm font-black animate-pulse">★</span>
                </div>

                {/* Pullman Coach Body (Expanded to fit 2x+ larger passenger observation windows) */}
                <div className="relative w-44 sm:w-52 h-16 sm:h-18 rounded-lg bg-gradient-to-b from-slate-800 via-slate-900 to-slate-950 border-2 border-amber-400/80 shadow-[0_6px_16px_rgba(0,0,0,0.75),inset_0_2px_4px_rgba(255,255,255,0.2)] flex flex-col justify-between p-1.5">
                  {/* Coach Roof Overhang */}
                  <div className="w-full h-1.5 rounded-t bg-amber-500/80 -mt-2 shadow-sm" />

                  {/* Arched Glowing Windows with 2x+ Enlarged Loser Avatars */}
                  <div className="flex justify-around items-center px-1 my-auto w-full gap-2">
                    {(() => {
                      const passengerList: (BostonTrainPassenger | undefined)[] =
                        losers && losers.length > 0
                          ? losers.length === 1
                            ? [losers[0], undefined]
                            : losers
                          : [undefined, undefined];

                      return passengerList.map((passenger, winIdx) => (
                        <div
                          key={`coach-win-${winIdx}`}
                          data-testid={passenger ? `loser-passenger-window-${winIdx}` : `train-window-${winIdx}`}
                          title={passenger?.name ? `${passenger.name} sent to Boston!` : undefined}
                          className="relative w-11 sm:w-13 h-11 sm:h-13 rounded-t-full bg-amber-200/90 border-2 border-amber-500 shadow-[0_0_10px_rgba(252,211,77,0.8)] flex items-end justify-center overflow-hidden"
                        >
                          {/* Window glass sheen */}
                          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/25 to-white/40 pointer-events-none z-10" />

                          {passenger ? (
                            <div
                              data-testid="loser-avatar"
                              className="relative z-0 w-9.5 sm:w-11.5 h-9.5 sm:h-11.5 rounded-full overflow-hidden flex items-center justify-center bg-slate-900 border-2 border-amber-400/90 shadow-sm mb-0.5"
                            >
                              {passenger.avatarUrl ? (
                                <img
                                  src={passenger.avatarUrl}
                                  alt={passenger.name || 'Loser'}
                                  className="w-full h-full object-cover"
                                  onError={(e) => {
                                    (e.target as HTMLElement).style.display = 'none';
                                  }}
                                />
                              ) : passenger.isBot ? (
                                <Bot className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400" />
                              ) : (
                                <span className="text-xs sm:text-sm font-black text-white">
                                  {passenger.name ? passenger.name.slice(0, 2).toUpperCase() : '??'}
                                </span>
                              )}
                              {/* Tiny sad tear drop overlay */}
                              <span
                                aria-hidden="true"
                                className="absolute -top-0.5 -right-0.5 text-xs sm:text-sm leading-none pointer-events-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                              >
                                💧
                              </span>
                            </div>
                          ) : (
                            /* Passenger Silhouette */
                            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-slate-900/80 mb-0.5" />
                          )}
                        </div>
                      ));
                    })()}
                  </div>

                  {/* Coach Gold Baseboard Trim */}
                  <div className="w-full h-1 bg-amber-400/70 rounded-full flex items-center justify-between px-2">
                    <span className="text-[7px] text-amber-200 font-mono font-bold tracking-tight">
                      {isUserTeam ? 'EXPRESS 77' : `TEAM ${winningTeam}`}
                    </span>
                    <span className="text-[7px] text-amber-200 font-mono font-bold tracking-tight">♠ ♥ ♦ ♣</span>
                  </div>
                </div>

                {/* Coach Wheels (4 heavy-duty Pullman wheels) */}
                <div className="w-full flex justify-between px-3 -mt-1.5 z-10">
                  <div className="boston-train-wheel w-4 h-4 rounded-full bg-slate-950 border-2 border-amber-400/90 shadow flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/40" />
                    <div className="absolute h-full w-[1px] bg-amber-400/40" />
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-300 z-10" />
                  </div>
                  <div className="boston-train-wheel w-4 h-4 rounded-full bg-slate-950 border-2 border-amber-400/90 shadow flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/40" />
                    <div className="absolute h-full w-[1px] bg-amber-400/40" />
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-300 z-10" />
                  </div>
                  <div className="boston-train-wheel w-4 h-4 rounded-full bg-slate-950 border-2 border-amber-400/90 shadow flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/40" />
                    <div className="absolute h-full w-[1px] bg-amber-400/40" />
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-300 z-10" />
                  </div>
                  <div className="boston-train-wheel w-4 h-4 rounded-full bg-slate-950 border-2 border-amber-400/90 shadow flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/40" />
                    <div className="absolute h-full w-[1px] bg-amber-400/40" />
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-300 z-10" />
                  </div>
                </div>
              </div>

              {/* Coupler / Connector */}
              <div className="w-2.5 h-1.5 bg-amber-400/90 rounded -mx-0.5 mb-2 shadow z-10" />

              {/* --- COAL TENDER CAR --- */}
              <div className="relative flex flex-col items-center mr-1">
                {/* Coal Mound */}
                <div className="w-14 sm:w-16 h-3 rounded-t-full bg-slate-950 border-t border-slate-700 shadow-inner -mb-0.5 z-10" />
                {/* Tender Body */}
                <div className="relative w-16 sm:w-19 h-11 sm:h-12 rounded-sm bg-gradient-to-b from-slate-800 via-slate-900 to-slate-950 border-2 border-amber-400/70 shadow-[0_6px_16px_rgba(0,0,0,0.75)] p-1 flex items-center justify-center">
                  <span
                    data-testid="tender-route-banner"
                    className="text-[7.5px] sm:text-[8px] font-mono font-black text-amber-300/90 tracking-tight whitespace-nowrap"
                  >
                    ATL -&gt; BOS
                  </span>
                </div>
                {/* Tender Wheels */}
                <div className="w-full flex justify-around px-1.5 -mt-1.5 z-10">
                  <div className="boston-train-wheel w-3.5 h-3.5 rounded-full bg-slate-950 border-2 border-amber-400 shadow flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/40" />
                    <div className="absolute h-full w-[1px] bg-amber-400/40" />
                    <div className="w-1 h-1 rounded-full bg-amber-300 z-10" />
                  </div>
                  <div className="boston-train-wheel w-3.5 h-3.5 rounded-full bg-slate-950 border-2 border-amber-400 shadow flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/40" />
                    <div className="absolute h-full w-[1px] bg-amber-400/40" />
                    <div className="w-1 h-1 rounded-full bg-amber-300 z-10" />
                  </div>
                </div>
              </div>

              {/* Coupler */}
              <div className="w-2.5 h-1.5 bg-amber-400/90 rounded -mx-0.5 mb-2 shadow z-10" />

              {/* --- STEAM LOCOMOTIVE ENGINE --- */}
              <div className="relative flex flex-col items-start">
                {/* Billowing Steam Clouds from Smokestack (Compositor-driven soft radial particles) */}
                <div className="absolute top-[-10px] right-7 pointer-events-none">
                  {STEAM_PUFFS.map((puff, idx) => (
                    <div
                      key={`steam-${idx}`}
                      className="boston-train-steam-puff absolute rounded-full"
                      style={{
                        width: `${puff.size}px`,
                        height: `${puff.size}px`,
                        left: `-${puff.size / 2}px`,
                        top: `-${puff.size / 2}px`,
                        animationDelay: `${puff.delay}s`,
                      }}
                    />
                  ))}
                </div>

                {/* Whistle Steam Choo Choo Bubble */}
                <div
                  className="boston-train-toot absolute -top-8 right-2 sm:right-3 px-2 sm:px-2.5 py-0.5 rounded-full bg-amber-100 text-slate-950 font-black text-[9px] sm:text-[9.5px] tracking-tight border border-amber-300 shadow-[0_2px_6px_rgba(0,0,0,0.6)] flex items-center gap-1 z-30 whitespace-nowrap"
                >
                  <span>💨</span>
                  <span data-testid="choo-choo-bubble">CHOO CHOO!</span>
                </div>

                {/* Engine Structure */}
                <div className="relative flex items-end">
                  {/* Engineer Cab */}
                  <div className="relative w-13 sm:w-15 h-16 sm:h-18 rounded-t bg-gradient-to-b from-slate-800 via-slate-900 to-slate-950 border-2 border-amber-400/90 shadow-[0_6px_16px_rgba(0,0,0,0.75)] flex flex-col justify-between p-1 z-10">
                    {/* Cab Roof Overhang */}
                    <div className="w-full h-1.5 rounded bg-amber-400 -mt-1.5 shadow-sm" />
                    {/* Cab Window */}
                    <div className="w-6 h-6 rounded-t-md bg-amber-300/95 border border-amber-500 mx-auto shadow-[0_0_10px_rgba(252,211,77,0.9)] flex items-end justify-center overflow-hidden">
                      <div className="w-3.5 h-4 bg-slate-950 rounded-t-full" />
                    </div>
                    {/* Cab Base */}
                    <div className="w-full h-1 bg-amber-500/80 rounded-full" />
                  </div>

                  {/* Cylindrical Steam Boiler Body */}
                  <div className="relative w-20 sm:w-24 h-11 sm:h-12 rounded-r-xl bg-gradient-to-b from-slate-700 via-slate-900 to-slate-950 border-2 border-amber-400/90 shadow-[0_6px_16px_rgba(0,0,0,0.75),inset_0_2px_4px_rgba(255,255,255,0.15)] flex items-center justify-around z-10">
                    {/* Brass Boiler Rings */}
                    <div className="w-1 h-full bg-amber-400/70" />
                    <div className="w-1 h-full bg-amber-400/70" />
                    <div className="w-1 h-full bg-amber-400/70" />

                    {/* Steam Dome & Whistle on top */}
                    <div className="absolute -top-3.5 left-4 w-4 h-3.5 rounded-t-full bg-amber-400 border border-amber-300 shadow-sm" />
                    <div className="absolute -top-5 left-6 w-1 h-3 bg-amber-200" />

                    {/* Tall Smokestack near front */}
                    <div className="absolute -top-5 right-5 w-4 h-5 rounded-t bg-gradient-to-t from-slate-800 to-slate-700 border-x-2 border-t-2 border-amber-400 flex flex-col items-center">
                      <div className="w-6 h-1.5 rounded-full bg-amber-400 -mt-0.5 shadow-sm" />
                    </div>

                    {/* Bright Glowing Headlight with Forward Light Beam */}
                    <div className="absolute -right-3 top-2 flex items-center">
                      {/* Brass Lantern */}
                      <div className="w-4 h-5 rounded-r bg-amber-400 border-2 border-amber-200 shadow-[0_0_12px_rgba(245,158,11,1)] flex items-center justify-center">
                        <div className="w-2 h-3 rounded-full bg-white animate-pulse" />
                      </div>
                      {/* Projected Light Beam Cone (Crisp polygon with gradient, no blur filter) */}
                      <div
                        className="w-20 sm:w-28 h-10 bg-gradient-to-r from-amber-300/40 via-amber-200/15 to-transparent pointer-events-none rounded-r-full"
                        style={{ clipPath: 'polygon(0% 35%, 100% 0%, 100% 100%, 0% 65%)' }}
                      />
                    </div>
                  </div>

                  {/* Cowcatcher / Pilot Grill on Front */}
                  <div className="relative -ml-1 mb-1 z-10">
                    <div className="w-4 sm:w-5 h-6 bg-gradient-to-br from-amber-400 via-slate-800 to-slate-950 border border-amber-300 skew-x-[25deg] shadow-lg flex flex-col justify-around p-0.5">
                      <div className="w-full h-0.5 bg-amber-200" />
                      <div className="w-full h-0.5 bg-amber-200" />
                      <div className="w-full h-0.5 bg-amber-200" />
                    </div>
                  </div>
                </div>

                {/* Locomotive Drive Wheels & Pushrod */}
                <div className="relative w-full flex items-center justify-between px-2 -mt-2 z-20">
                  {/* Large Front Driver Wheel */}
                  <div className="boston-train-wheel w-5 sm:w-6 h-5 sm:h-6 rounded-full bg-slate-950 border-2 border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.6)] flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/50" />
                    <div className="absolute h-full w-[1px] bg-amber-400/50" />
                    <div className="w-2 h-2 rounded-full bg-amber-300 z-10" />
                  </div>
                  {/* Large Middle Driver Wheel */}
                  <div className="boston-train-wheel w-5 sm:w-6 h-5 sm:h-6 rounded-full bg-slate-950 border-2 border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.6)] flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/50" />
                    <div className="absolute h-full w-[1px] bg-amber-400/50" />
                    <div className="w-2 h-2 rounded-full bg-amber-300 z-10" />
                  </div>
                  {/* Large Rear Driver Wheel */}
                  <div className="boston-train-wheel w-5 sm:w-6 h-5 sm:h-6 rounded-full bg-slate-950 border-2 border-amber-400 shadow-[0_0_8px_rgba(245,158,11,0.6)] flex items-center justify-center relative overflow-hidden">
                    <div className="absolute w-full h-[1px] bg-amber-400/50" />
                    <div className="absolute h-full w-[1px] bg-amber-400/50" />
                    <div className="w-2 h-2 rounded-full bg-amber-300 z-10" />
                  </div>
                  {/* Small Leading Wheel */}
                  <div className="boston-train-wheel w-3.5 h-3.5 rounded-full bg-slate-950 border-2 border-amber-400 shadow flex items-center justify-center relative overflow-hidden -mr-1">
                    <div className="absolute w-full h-[1px] bg-amber-400/50" />
                    <div className="absolute h-full w-[1px] bg-amber-400/50" />
                    <div className="w-1 h-1 rounded-full bg-amber-300 z-10" />
                  </div>

                  {/* Connecting Rod Connecting the Driving Wheels */}
                  <div className="boston-train-rod absolute top-1/2 left-3 right-5 h-1 bg-amber-300 border border-amber-500 rounded-full opacity-90 shadow-sm pointer-events-none" />
                </div>
              </div>
            </div>
          </div>

          <style>{`
            .boston-train-traverse {
              animation: bostonTrainTraverse 8.0s linear infinite;
              transform: translate3d(-540px, 0, 0);
              will-change: transform;
              backface-visibility: hidden;
            }

            .boston-train-chug {
              animation: bostonChug 0.42s ease-in-out infinite;
              will-change: transform;
              backface-visibility: hidden;
            }

            .boston-train-marquee {
              animation: bostonMarqueePulse 1.5s ease-in-out infinite;
              will-change: transform;
            }

            .boston-train-toot {
              animation: bostonTootFloat 1.2s ease-in-out infinite;
              will-change: transform, opacity;
            }

            .boston-train-wheel {
              animation: bostonWheelSpin 0.42s linear infinite;
              will-change: transform;
              transform-origin: center center;
            }

            .boston-train-rod {
              animation: bostonRodMotion 0.42s ease-in-out infinite;
              will-change: transform;
              transform: translate3d(0, -50%, 0);
            }

            .boston-train-steam-puff {
              background: radial-gradient(circle, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.6) 45%, rgba(255, 255, 255, 0) 80%);
              animation: bostonSteamPuff 1.6s cubic-bezier(0.2, 0.6, 0.35, 1) infinite;
              will-change: transform, opacity;
              opacity: 0;
            }

            @keyframes bostonTrainTraverse {
              0% {
                transform: translate3d(-540px, 0, 0);
              }
              85% {
                transform: translate3d(calc(100vw + 60px), 0, 0);
              }
              100% {
                transform: translate3d(calc(100vw + 60px), 0, 0);
              }
            }

            @keyframes bostonChug {
              0%, 100% {
                transform: translate3d(0, 0, 0);
              }
              25% {
                transform: translate3d(0, -2px, 0);
              }
              50% {
                transform: translate3d(0, 0, 0);
              }
              75% {
                transform: translate3d(0, -2px, 0);
              }
            }

            @keyframes bostonMarqueePulse {
              0%, 100% {
                transform: scale3d(1, 1, 1);
              }
              50% {
                transform: scale3d(1.03, 1.03, 1);
              }
            }

            @keyframes bostonTootFloat {
              0%, 100% {
                transform: translate3d(0, 0, 0) scale3d(0.98, 0.98, 1);
                opacity: 0.85;
              }
              50% {
                transform: translate3d(0, -1.5px, 0) scale3d(1.04, 1.04, 1);
                opacity: 1;
              }
            }

            @keyframes bostonWheelSpin {
              from {
                transform: rotate(0deg);
              }
              to {
                transform: rotate(360deg);
              }
            }

            @keyframes bostonRodMotion {
              0%, 100% {
                transform: translate3d(0, -50%, 0);
              }
              25% {
                transform: translate3d(1px, calc(-50% - 1.5px), 0);
              }
              50% {
                transform: translate3d(0, -50%, 0);
              }
              75% {
                transform: translate3d(-1px, calc(-50% + 1.5px), 0);
              }
            }

            @keyframes bostonSteamPuff {
              0% {
                opacity: 0;
                transform: translate3d(0, 0, 0) scale(0.4);
              }
              15% {
                opacity: 0.85;
                transform: translate3d(-12px, -12px, 0) scale(0.95);
              }
              55% {
                opacity: 0.6;
                transform: translate3d(-34px, -28px, 0) scale(1.6);
              }
              100% {
                opacity: 0;
                transform: translate3d(-55px, -45px, 0) scale(2.2);
              }
            }

            @media (prefers-reduced-motion: reduce) {
              .boston-train-traverse {
                animation-duration: 12s !important;
              }
              .boston-train-chug,
              .boston-train-wheel,
              .boston-train-rod,
              .boston-train-marquee,
              .boston-train-toot {
                animation: none !important;
              }
            }
          `}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const BostonTrainAnimation = React.memo(BostonTrainAnimationComponent);
